import React, { useEffect, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { Field, inputCls, monoCls, selectCls, Modal } from '../admin/Field';
import { adminListRoomsForOrg } from '../../lib/admin';
import { listGatewayHwTypes, normMac, fmtMac } from '../../lib/gateways';

// Create (super-admin) or edit a gateway. Org admins may only change name, room and notes.
export function GatewayForm({ gateway, orgs, isSuper, defaultOrgId, knownHwTypes = [], onCancel, onSave, busy, error }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    gateway_name: gateway?.gateway_name || '',
    gateway_mac_hex: gateway?.gateway_mac_hex ? fmtMac(gateway.gateway_mac_hex) : '',
    hw_type_id: gateway?.hw_type_id != null ? String(gateway.hw_type_id) : '',
    organization_id: gateway?.organization_id || defaultOrgId || '',
    room_id: gateway?.room_id || '',
    notes: gateway?.notes || '',
  });
  const [rooms, setRooms] = useState([]);
  const [hwTypes, setHwTypes] = useState([]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    listGatewayHwTypes().then((l) => setHwTypes(l.length ? l : knownHwTypes)).catch(() => setHwTypes(knownHwTypes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!form.organization_id) { setRooms([]); return; }
    adminListRoomsForOrg(form.organization_id).then(setRooms).catch(() => setRooms([]));
  }, [form.organization_id]);

  const macOk = normMac(form.gateway_mac_hex).length === 12;
  const valid = form.gateway_name.trim() && macOk && form.hw_type_id && form.organization_id && form.room_id;

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    const body = { gateway_name: form.gateway_name.trim(), room_id: form.room_id, notes: form.notes.trim() || null };
    if (isSuper) Object.assign(body, { gateway_mac_hex: normMac(form.gateway_mac_hex), hw_type_id: Number(form.hw_type_id), organization_id: form.organization_id });
    onSave(body);
  };

  return (
    <Modal onCancel={onCancel}>
      <form onSubmit={submit} className="space-y-4" data-testid="gateway-form">
        <h2 className="font-heading text-2xl text-ink">{gateway ? t('gw_edit_title') : t('gw_new_title')}</h2>
        {error && <div className="bg-terracotta-50 text-terracotta-700 rounded-lg p-3 text-sm" data-testid="gateway-form-error">{error}</div>}
        <Field label={t('admin_field_name')} required>
          <input className={inputCls} value={form.gateway_name} onChange={set('gateway_name')} data-testid="gateway-form-name" required />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="MAC" required hint={!isSuper ? t('gw_super_field') : form.gateway_mac_hex && !macOk ? t('gw_mac_invalid') : null}>
            <input className={monoCls} value={form.gateway_mac_hex} onChange={set('gateway_mac_hex')} disabled={!isSuper}
              placeholder="AA:BB:CC:DD:EE:FF" data-testid="gateway-form-mac" />
          </Field>
          <Field label={t('gw_hw_type')} required>
            <select className={selectCls} value={form.hw_type_id} onChange={set('hw_type_id')} disabled={!isSuper} data-testid="gateway-form-hw">
              <option value="">—</option>
              {hwTypes.map((h) => <option key={h.id} value={String(h.id)}>{h.model ? `${h.name} · ${h.model}` : h.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label={t('admin_field_organization')} required hint={!isSuper ? t('gw_super_field') : null}>
          <select className={selectCls} value={form.organization_id} disabled={!isSuper}
            onChange={(e) => setForm((f) => ({ ...f, organization_id: e.target.value, room_id: '' }))} data-testid="gateway-form-org">
            <option value="">—</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </Field>
        <Field label={t('gw_col_room')} required>
          <select className={selectCls} value={form.room_id} onChange={set('room_id')} disabled={!form.organization_id} data-testid="gateway-form-room">
            <option value="">—</option>
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.room_name || r.name}</option>)}
          </select>
        </Field>
        <Field label={t('gw_notes')}>
          <textarea className={`${inputCls} min-h-[72px]`} value={form.notes} onChange={set('notes')} data-testid="gateway-form-notes" />
        </Field>
        <div className="flex justify-end gap-2 pt-2 border-t border-stone_line">
          <button type="button" onClick={onCancel} disabled={busy} className="px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-bg-alt disabled:opacity-50" data-testid="gateway-form-cancel">{t('cancel')}</button>
          <button type="submit" disabled={busy || !valid} className="px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50" data-testid="gateway-form-save">{busy ? '…' : t('save')}</button>
        </div>
      </form>
    </Modal>
  );
}
